import { Icon } from '@iconify/react'
import { Link } from 'react-router-dom'
import { useState } from 'react'
import { Seo } from '@/seo/Seo'
import { useBranding } from '@/branding/BrandingProvider'
import { api, ApiError } from '@/api/client'

/**
 * Step 1 of password recovery: ask for the email, have the backend mail a reset
 * link. `POST /api/auth/forgot-password` answers 201 when a mail was sent and 200
 * when the address is unknown — deliberately indistinguishable to the caller, so
 * we always show the same confirmation and never leak whether an account exists.
 *
 * This page is reached from the "Forgot password?" link on /sign-in, which
 * previously pointed at a route that did not exist (react-router 404 → the
 * "Unexpected Application Error!" boundary), leaving locked-out users with no
 * way back in. Client-only: not prerendered, noindex.
 */
export function ForgotPasswordPage() {
  const { siteName } = useBranding()
  const [email, setEmail] = useState('')
  const [sent, setSent] = useState(false)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (loading) return
    setError('')
    setLoading(true)
    try {
      await api.post('/auth/forgot-password', { email: email.trim().toLowerCase() })
      setSent(true)
    } catch (err) {
      // 200/201 both resolve; anything here is a genuine transport/server fault.
      const status = err instanceof ApiError ? err.status : undefined
      setError(
        status === 429
          ? 'Too many requests. Please wait a moment and try again.'
          : 'Unable to send the reset link right now. Please try again in a moment.',
      )
    } finally {
      setLoading(false)
    }
  }

  return (
    <main className="min-h-screen flex items-center justify-center bg-white font-sans px-6 py-12">
      <Seo
        title={`Reset Your Password — ${siteName}`}
        description={`Request a password reset link for your ${siteName} account.`}
        path="/forgot-password"
        noindex
      />
      <div className="w-full max-w-md">
        <Link
          to="/sign-in"
          className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-navy mb-12 transition-colors"
        >
          <Icon icon="solar:arrow-left-linear" /> Back to Sign In
        </Link>

        {sent ? (
          <div>
            <div className="w-12 h-12 rounded-full bg-green-50 flex items-center justify-center mb-6">
              <Icon icon="solar:letter-linear" className="text-2xl text-green-600" />
            </div>
            <h1 className="text-3xl font-medium tracking-tight text-navy mb-2">Check your email</h1>
            <p className="text-slate-500 font-light leading-relaxed">
              If an account exists for <span className="font-medium text-navy">{email}</span>, we&apos;ve sent a link to
              reset your password. The link expires shortly, so use it soon.
            </p>
            <p className="text-sm text-slate-400 font-light mt-4">
              Don&apos;t see it? Check your spam folder, or{' '}
              <button
                type="button"
                onClick={() => { setSent(false); setError('') }}
                className="text-rust hover:text-navy transition-colors font-medium"
              >
                try a different address
              </button>
              .
            </p>
          </div>
        ) : (
          <>
            <div className="mb-10">
              <h1 className="text-3xl font-medium tracking-tight text-navy mb-2">Forgot your password?</h1>
              <p className="text-slate-500 font-light">
                Enter the email you signed up with and we&apos;ll send you a link to set a new password.
              </p>
            </div>

            {error && (
              <div className="mb-6 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">{error}</div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-1">
                  Email
                </label>
                <input
                  id="email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="w-full px-4 py-3 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-navy/20 focus:border-navy outline-none"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-navy text-white py-3 rounded-lg font-medium text-sm hover:bg-slate-800 transition-colors shadow-sm disabled:opacity-50"
              >
                {loading ? 'Sending...' : 'Send Reset Link'}
              </button>
            </form>
          </>
        )}
      </div>
    </main>
  )
}
